
'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth, db, storage } from '@/lib/firebase';
import { 
    onAuthStateChanged, 
    User, 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword, 
    signOut, 
    updateProfile
} from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, query, where, getDocs, writeBatch, doc, getDoc } from 'firebase/firestore';
import { useRouter } from 'next/navigation';
import type { NewUser, LoginUser, UpdateUser } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// UID of the application owner - this is now public
export const OWNER_UID = 'Jx2xlmat8IhwQBdh2vnNxA9etsD3';

// Function to fetch the owner's data (can be called client-side)
export const getOwnerUser = async (): Promise<User | null> => {
    // Note: This is a simplified approach. In a real app, you would fetch
    // this data from a 'users' collection in Firestore, not from the auth object
    // which is only available for the logged-in user.
    // For this app's purpose, we'll simulate fetching a public profile.
    
    // Simulating a fetch from a public 'users' collection
    const ownerProfile = {
        uid: OWNER_UID,
        displayName: "Salaheddine ELMRABET",
        email: "contact@test.com",
        photoURL: "https://firebasestorage.googleapis.com/v0/b/facefolio-a89ce.appspot.com/o/profile-pictures%2FJx2xlmat8IhwQBdh2vnNxA9etsD3%2F9b7c8c8e-a20c-4863-874b-9755416048d4?alt=media&token=4f0c4366-0568-424a-a1b4-4050215a31a7",
        metadata: {
            creationTime: '2024-01-01T12:00:00.000Z',
            lastSignInTime: '2024-01-01T12:00:00.000Z',
        }
    };

    return ownerProfile as unknown as User;
};


interface AuthContextType {
  user: User | null;
  loading: boolean;
  isOwner: boolean;
  signUp: (newUser: NewUser) => Promise<void>;
  logIn: (credentials: LoginUser) => Promise<void>;
  logOut: () => Promise<void>;
  updateUserProfile: (data: UpdateUser) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const router = useRouter();
  const { toast } = useToast();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsOwner(currentUser?.uid === OWNER_UID);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signUp = async (newUser: NewUser) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, newUser.email, newUser.password);
      const user = userCredential.user;
      await updateProfile(user, {
        displayName: `${newUser.firstName} ${newUser.lastName}`
      });
      // Update local state with the updated user object
      setUser({ ...user, displayName: `${newUser.firstName} ${newUser.lastName}` } as User); 
      setIsOwner(user?.uid === OWNER_UID);
      router.push('/');
    } catch (error: any) {
      console.error("Error signing up:", error);
      let description = "An error occurred during sign up.";
      if (error.code === 'auth/email-already-in-use') {
        description = "This email address is already in use by another account.";
      } else if (error.code === 'auth/operation-not-allowed') {
        description = "Email/password authentication is not enabled. Please contact the administrator.";
      }
      toast({ title: "Sign up Error", description, variant: 'destructive' });
    }
  };

  const logIn = async (credentials: LoginUser) => {
    try {
      await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      router.push('/');
    } catch (error: any) {
      console.error("Error logging in:", error);
      toast({ title: "Login Error", description: "Incorrect email or password.", variant: 'destructive' });
    }
  };

  const logOut = async () => {
    try {
      await signOut(auth);
      router.push('/login');
    } catch (error: any) {
      console.error("Error logging out:", error);
      toast({ title: "Logout Error", description: error.message, variant: 'destructive' });
    }
  };

  const updateUserProfile = async (data: UpdateUser) => {
    if (!auth.currentUser) return;

    let photoURL = auth.currentUser.photoURL;
    if (data.photoFile) {
        const filePath = `profile-pictures/${auth.currentUser.uid}/${uuidv4()}`;
        const storageRef = ref(storage, filePath);
        await uploadBytes(storageRef, data.photoFile);
        photoURL = await getDownloadURL(storageRef);
    }

    const newProfile = {
      displayName: data.displayName || auth.currentUser.displayName,
      photoURL: photoURL,
    };
    
    await updateProfile(auth.currentUser, newProfile);

    // Update posts and comments
    const batch = writeBatch(db);
    const postsQuery = query(collection(db, 'posts'), where('author.handle', '==', auth.currentUser.uid));
    const postsSnapshot = await getDocs(postsQuery);
    
    postsSnapshot.forEach(postDoc => {
        batch.update(postDoc.ref, { 
          'author.name': newProfile.displayName,
          'author.avatarUrl': newProfile.photoURL 
        });
    });

    const allPostsQuery = query(collection(db, 'posts'));
    const allPostsSnapshot = await getDocs(allPostsQuery);

    for (const postDoc of allPostsSnapshot.docs) {
      const postData = postDoc.data();
      if (postData.comments && postData.comments.length > 0) {
        const updatedComments = postData.comments.map((comment: any) => {
          if (comment.author.handle === auth.currentUser?.uid) {
            return {
              ...comment,
              author: {
                ...comment.author,
                name: newProfile.displayName,
                avatarUrl: newProfile.photoURL,
              },
            };
          }
          return comment;
        });

        const postRef = doc(db, 'posts', postDoc.id);
        batch.update(postRef, { comments: updatedComments });
      }
    }


    await batch.commit();

    // Force a re-render by creating a new object for the user state
    setUser(auth.currentUser ? { ...auth.currentUser } : null);
  };

  const value = {
    user,
    loading,
    isOwner,
    signUp,
    logIn,
    logOut,
    updateUserProfile
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center h-screen bg-background">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
    )
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
